/* eslint-disable no-lonely-if */
/* eslint-disable no-param-reassign */
import React from "react";
import dayjs from "dayjs";
import { makeObservable, observable, action } from "mobx";
import { Props as DataGridProps } from "../components/DataGrid/DataGrid";
import { getTemplates } from "../api/Templates";
import {
  UtcDateFormatter,
  setStartFormat,
  setEndFormat,
} from "../utils/dateformat/dateFormat";
import { Cookies } from "react-cookie";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PushPinIcon from "@mui/icons-material/PushPin";
import { defaultAllCode } from "../pages/Templates/LanguageCode";
import { defaultAllCategory, getCategoryCode } from "../pages/Templates/CategoryCode";
import * as Assets from "../components/Icon/assets";
import { Typography } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const cookies = new Cookies();
const getCookie = (item: string) => {
  return cookies.get(item);
};

// 사용자가 필터에서 날짜를 직접 지정했는지 여부. ai_dateExplicit 쿠키가 "1"이면
// 명시적 날짜 필터가 살아있다는 뜻이며, 이때는 그 값을 복원해 우선한다.
const isExplicitDateCookie = (): boolean => getCookie("ai_dateExplicit") === "1";

// 진입/초기화 시 startDate 우선순위:
// 1) 명시적 날짜 필터(ai_startDate, ai_dateExplicit="1") — 항상 우선하며 유지된다.
// 2) 없으면 오늘 0시를 기본값으로 쓴다.
const resolveEntryStartDate = (): Date => {
  if (isExplicitDateCookie() && getCookie("ai_startDate")) {
    return dayjs(getCookie("ai_startDate")).toDate();
  }
  return dayjs().startOf("day").toDate();
};

const resolveEntryEndDate = (): Date => {
  if (isExplicitDateCookie() && getCookie("ai_endDate")) {
    return dayjs(getCookie("ai_endDate")).toDate();
  }
  return dayjs().toDate();
};

class AIReviewStore {
  constructor() {
    makeObservable(this, {
      application: observable,
      status: observable,
      startDate: observable,
      endDate: observable,
      gridOption: observable,
      headerStatus: observable,
      language: observable,
      category: observable,
      badge: observable,
      isBlind: observable,
      sortKey: observable,
      isDateExplicit: observable,
    });
  }

  application = getCookie("ai_application") ? getCookie("ai_application") : "all";
  status = getCookie("ai_status") ? getCookie("ai_status") : "All";

  nowDate = dayjs().toDate();

  // 사용자가 날짜 피커를 직접 건드려 적용한 명시적 날짜 필터가 있는지.
  isDateExplicit: boolean = isExplicitDateCookie();

  startDate: Date = resolveEntryStartDate();

  endDate: Date = resolveEntryEndDate();

  headerStatus = localStorage.getItem("ai_columns") || "";
  language = getCookie("ai_language") ? getCookie("ai_language") : defaultAllCode;
  category = getCookie("ai_category")
    ? getCookie("ai_category")
    : defaultAllCategory;

  badge = "none";
  isBlind = false;

  sortKey = getCookie("ai_sortKey") ? getCookie("ai_sortKey") : "createdAt";

  gridOption: DataGridProps = {
    columns: [
      {
        field: "index",
        headerName: "No",
        filterable: false,
        renderCell: (index) =>
          index.api.getRowIndex(index.row.id) +
          1 +
          this.gridOption.page * this.gridOption.rowsPerPage,
        width: 50,
        align: "center",
        hide: this.headerStatus.includes("index"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "thumbnail",
        headerName: "Thumbnail",
        width: 120,
        renderCell: (param) => (
          <div
            style={{ height: "100%", display: "flex", alignItems: "center" }}
          >
            <img
              style={{ height: "calc(100% - 5px)", maxWidth: "100px" }}
              src={param.row.thumbnail}
              alt=""
            />
          </div>
        ),
        hide: this.headerStatus.includes("thumbnail"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "modResult",
        headerName: "Mod",
        width: 110,
        sortable: false,
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const modResult = param.row.aiReview?.moderation?.result;
          return (
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color:
                  modResult === "Published"
                    ? "#1976D2"
                    : modResult === "Rejected"
                    ? "#B3261E"
                    : "inherit",
              }}
            >
              {modResult ?? "N/A"}
            </Typography>
          );
        },
      },
      {
        field: "minAge",
        headerName: "Age",
        width: 70,
        sortable: false,
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const minAge = param.row.aiReview?.ageRating?.minAge;
          return (
            <Typography variant="body2">
              {minAge !== undefined ? `${minAge}+` : "N/A"}
            </Typography>
          );
        },
      },
      {
        field: "totalScore",
        headerName: "Total",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.totalScore;
          return (
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {v != null ? v : "N/A"}
            </Typography>
          );
        },
        renderHeader: (params) => this.renderSortHeader(params, "totalScore"),
      },
      {
        field: "videoEditingScore",
        headerName: "Editing",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.videoEditingScore;
          return <Typography variant="body2">{v != null ? v : "N/A"}</Typography>;
        },
        renderHeader: (params) => this.renderSortHeader(params, "videoEditingScore"),
      },
      {
        field: "videoQualityScore",
        headerName: "Quality",
        width: 90,
        sortable: false,
        renderCell: (param: any) => {
          const v = param.row.aiReview?.quality?.videoQualityScore;
          return <Typography variant="body2">{v != null ? v : "N/A"}</Typography>;
        },
        renderHeader: (params) => this.renderSortHeader(params, "videoQualityScore"),
      },
      {
        field: "userId",
        headerName: "User ID",
        width: 120,
        hide: this.headerStatus.includes("userId"),
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "lang",
        headerName: "Lang",
        width: 75,
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "region",
        headerName: "Region",
        width: 110,
        headerClassName: "ai-ungrouped-header",
      },
      {
        field: "reviewStatus",
        headerName: "Status",
        width: 140,
        hide: this.headerStatus.includes("reviewer"),
        headerClassName: "ai-ungrouped-header",
        renderCell: (param) => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {param.row.isBlind ? (
                <Typography style={{ display: "flex", alignItems: "center" }}>
                  {param.row.reviewStatus}
                  <VisibilityOffIcon
                    style={{
                      marginLeft: "5px",
                      marginTop: "2px",
                      color: "#79747e",
                    }}
                  />
                </Typography>
              ) : (
                <Typography>{param.row.reviewStatus}</Typography>
              )}
            </div>
          );
        },
      },
      {
        field: "categories",
        headerName: "Category",
        width: 130,
        sortable: false,
        hide: this.headerStatus.includes("categories"),
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const labels = (param.row.categories ?? [])
            .map((c: any) => {
              const found = getCategoryCode.find((g) => g.value === c.id);
              return found ? { key: found.key, pinned: c.pinned } : null;
            })
            .filter(Boolean);
          if (labels.length === 0) return "";
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              {labels.map((l: any) => (
                <span
                  key={l.key}
                  style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
                >
                  <Typography variant="body2">{l.key}</Typography>
                  {l.key === "Recommended" && l.pinned && (
                    <PushPinIcon fontSize="small" sx={{ color: "#1976D2" }} />
                  )}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        field: "isThirdPartyLogo",
        headerName: "3rd Party Logo",
        width: 120,
        sortable: false,
        hide: this.headerStatus.includes("isThirdPartyLogo"),
        headerClassName: "ai-ungrouped-header",
        renderCell: (param: any) => {
          const has = param.row.isThirdPartyLogo;
          if (has == null) return <Typography variant="body2">N/A</Typography>;
          return (
            <Typography variant="body2">{has ? "Yes" : "No"}</Typography>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Uploaded Date",
        width: 240,
        hide: this.headerStatus.includes("createdAt"),
        headerClassName: "ai-ungrouped-header",
        renderHeader: (params) => this.renderSortHeader(params, "createdAt"),
      },
      {
        field: "link",
        headerName: "Link",
        width: 80,
        hide: this.headerStatus.includes("link"),
        headerClassName: "ai-ungrouped-header",
        renderCell: () => {
          return (
            <>
              <img
                src={Assets.NewWindows}
                alt="new window"
                style={{
                  width: 20,
                  height: 20,
                  cursor: "pointer",
                }}
              />
            </>
          );
        },
      },
    ],

    rows: [],
    totalCount: 0,
    loading: true,
    // AI Review는 last-visited로 진입마다 조회 범위가 바뀌어 이전 페이지 위치 복원이
    // 무의미하다. ai_page 쿠키를 신뢰하면 데이터가 줄었을 때 그 값이 범위를 벗어나
    // mount 후 DataGrid가 page를 자동 보정 → useDidMountEffect가 재발동해 setRows가
    // 두 번 호출된다. 항상 0으로 시작해 이 보정 자체를 없앤다.
    page: 0,
    rowsPerPage: Number(getCookie("ai_rowsPerPage"))
      ? Number(getCookie("ai_rowsPerPage"))
      : 100,
    sort: getCookie("ai_tSort") ? getCookie("ai_tSort") : "desc",
    columnGroupingModel: [
      {
        groupId: "score",
        headerName: "Score",
        children: [
          { field: "totalScore" },
          { field: "videoEditingScore" },
          { field: "videoQualityScore" },
        ],
      },
    ],
  };

  setApplication = action((application: string) => {
    this.application = application;
  });

  setStatus = action((status: string) => {
    this.status = status;
  });

  setStartDate = action((startDate: Date) => {
    this.startDate = startDate;
  });

  setEndDate = action((endDate: Date) => {
    this.endDate = endDate;
  });

  setGridOption = action((gridOption: any) => {
    this.gridOption = {
      ...this.gridOption,
      ...gridOption,
    };
  });
  setLanguageChange = action((value: any) => {
    this.language = value;
  });

  setCategoryChange = action((category: any) => {
    this.category = category;
  });

  setBadge = action((badge: any) => {
    this.badge = badge;
  });

  setIsBlind = action((blind: boolean) => {
    this.isBlind = blind;
  });

  setSortKey = action((value: string) => {
    this.sortKey = value;
  });

  setDateExplicit = action((value: boolean) => {
    this.isDateExplicit = value;
  });

  renderSortHeader = (params: any, key: string) => {
    const active = this.sortKey === key;
    return (
      <div
        style={{
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        {params.colDef.headerName}
        {active && this.gridOption.sort === "desc" ? (
          <ArrowUpwardIcon fontSize="small" style={{ marginLeft: "5px" }} />
        ) : active && this.gridOption.sort === "asc" ? (
          <ArrowDownwardIcon fontSize="small" style={{ marginLeft: "5px" }} />
        ) : (
          <UnfoldMoreIcon
            fontSize="small"
            style={{ marginLeft: "5px", opacity: 0.35 }}
          />
        )}
      </div>
    );
  };

  onPageSizeChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.rowsPerPage = params;
  });

  onPageChange = action((params: any) => {
    window.scrollTo(0, 0);
    this.gridOption.page = params;
  });

  get getGridStatus() {
    return {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: this.startDate,
      endedAt: this.endDate,
      reviewStatus: this.status !== "All" ? this.status : undefined,
    };
  }

  setRows = action(async () => {
    this.setIsBlind(this.status === "Blind" ? true : false);

    // 명시적 날짜 필터(피커로 직접 지정)는 UTC 달력일 경계로 조회한다 — 표의 UTC
    // createdAt과 고른 날짜를 일치시키기 위함. 다른 탭(Templates/Moderation/History)이
    // API 레이어에서 쓰는 것과 동일한 setStartFormat/setEndFormat를 사용해 결과를
    // 일치시킨다. 기본(비명시) 경로는 정밀 시각을 유지한다(startedAt에 +1ms로
    // 경계가 겹치지 않게).
    const startedAtIso = this.isDateExplicit
      ? setStartFormat(this.startDate).toISOString()
      : dayjs(this.startDate).add(1, "millisecond").toISOString();
    const endedAtIso = this.isDateExplicit
      ? setEndFormat(this.endDate).toISOString()
      : dayjs(this.endDate).toISOString();

    const requestParameter = {
      page: this.gridOption.page + 1,
      display: this.gridOption.rowsPerPage,
      startedAt: startedAtIso,
      endedAt: endedAtIso,
      reviewStatus:
        this.status !== "All"
          ? this.status === "Blind"
            ? undefined
            : this.status
          : undefined,
      sort: this.gridOption.sort,
      language: this.language.value,
      category: this.category.value,
      application: this.application !== "all" ? this.application : "",
      isBlind: this.isBlind,
      sortKey: this.sortKey,
    };
    this.gridOption.loading = true;
    this.gridOption.rows = [];

    const blindParameter = {
      page: 1,
      display: 100,
      startedAt: startedAtIso,
      endedAt: endedAtIso,
      reviewStatus: undefined,
      sort: this.gridOption.sort,
      language: this.language.value,
      category: this.category.value,
      application: this.application !== "all" ? this.application : "",
      isBlind: true,
      sortKey: this.sortKey,
    };

    const allList: any[] = [
      getTemplates(requestParameter),
      getTemplates(blindParameter),
    ];

    await Promise.all(allList)
      .then((item) => {
        // Axios 는 실패해도 throw 하지 않고 에러 객체를 그대로 반환한다(=> Promise.all 도
        // 정상 resolve 한다). 정상 응답에만 있는 필드로 판별해 catch 로 넘긴다.
        // item[1] 은 blind merge 용 두 번째 호출 — 이쪽만 실패해도 병합 결과가 틀어진다.
        if (!item[0]?.templates || !item[1]?.templates) {
          throw item[0]?.templates ? item[1] : item[0];
        }
        const templateMap = new Map();
        const blindTemplateIds = new Set();
        const resultList: any[] = [];

        item[0].templates.forEach((template: any) => {
          templateMap.set(template.templateId, {
            ...template,
            isBlind: false,
          });
        });

        item[1].templates.forEach((template: any) => {
          blindTemplateIds.add(template.templateId);
        });

        templateMap.forEach((template) => {
          const isBlind = blindTemplateIds.has(template.templateId);
          resultList.push({
            ...template,
            isBlind,
          });
        });

        const mapped = resultList.map((info: any) => {
          const reviewer = info.reviewer.split("@");
          return {
            ...info,
            id: info.templateId,
            thumbnail: info.imagePath,
            description: info.translations[0].description,
            createdAt: UtcDateFormatter(info.createdAt),
            userId: info.author.sub,
            name: info.author.name,
            lang: info.metaLanguageCode,
            region: `${
              info.metaCountryCode.length >= 249
                ? "All Country"
                : info.metaCountryCode
            }`,
            reviewStatus:
              info.reviewStatus === "InReview"
                ? "In Review"
                : info.reviewStatus,
            reviewer: reviewer[0],
          };
        });

        this.setGridOption({
          totalCount: item[0].totalCount,
          rows: mapped,
        });
      })
      .catch((err) => {
        console.log("err", err);
        this.gridOption.totalCount = 0;
        const status = err?.response?.status;
        alert(
          `Failed to load the AI Review list.${status ? ` (code: ${status})` : ""}`
        );
      })
      .finally(() => {
        this.gridOption.loading = false;
      });
  });

  resetDates = action(() => {
    this.isDateExplicit = isExplicitDateCookie();
    // 명시적 날짜 필터가 있으면 그 값을 복원해 우선한다(resolveEntry*).
    // 없으면 startDate=오늘 0시, endDate=now 기본값을 쓴다.
    this.startDate = resolveEntryStartDate();
    this.endDate = resolveEntryEndDate();
  });

  searchHandler = action(async () => {
    this.gridOption.page = 0;
    await this.setRows();
  });
}

export default AIReviewStore;
